<template>
  <div>
    <div class="configurator-actions">
      <div>
        <div class="configurator-title">
          {{ $t('configurator.summary_title') }}
        </div>
        <div
          v-if="order"
          class="configurator-subtitle"
        >
          {{ subtitle }}
        </div>
      </div>
      <b-button
        v-if="editable"
        variant="outline-secondary"
        class="cfg-btn"
        @click="$emit('goto', 3)"
      >
        ← {{ $t('configurator.step_extras') }}
      </b-button>
    </div>

    <template v-if="!order">
      <a
        v-for="error in errorList"
        :key="error.text"
        href="#"
        class="budget-warning budget-error todo-link mb-50"
        @click.prevent="goToError(error)"
      >
        {{ error.text }}
        <feather-icon
          v-if="error.step"
          icon="ChevronRightIcon"
          size="13"
          class="ml-auto"
        />
      </a>
    </template>

    <div
      v-if="legacy"
      class="budget-note mb-1"
    >
      <feather-icon
        icon="InfoIcon"
        size="14"
      />
      {{ $t('configurator.legacy_offer') }}
    </div>

    <div
      v-if="editable"
      class="d-flex flex-wrap mb-1"
    >
      <b-button
        variant="outline-secondary"
        class="cfg-btn mr-50 mb-50"
        @click="$emit('goto', 1)"
      >
        <feather-icon
          icon="PlusIcon"
          size="14"
        />
        {{ $t('configurator.add_curtain') }}
      </b-button>
      <b-button
        variant="outline-secondary"
        class="cfg-btn mr-50 mb-50"
        @click="$emit('goto', 3)"
      >
        + {{ $t('configurator.add_box') }}
      </b-button>
      <b-button
        variant="outline-secondary"
        class="cfg-btn mb-50"
        @click="$emit('goto', 3)"
      >
        + {{ $t('configurator.add_extra') }}
      </b-button>
    </div>

    <offer-lines
      v-if="order"
      :order="order"
      :state="offerState"
    />

    <div
      v-if="order"
      class="summary-bottom"
    >
      <div class="cfg-card p-1">
        <div class="actions-title">
          {{ $t('configurator.actions') }}
        </div>
        <div
          v-if="editable"
          class="actions-grid"
        >
          <b-button
            variant="primary"
            class="cfg-btn-lg"
            @click="saveOffer(true)"
          >
            <feather-icon
              icon="SaveIcon"
              size="14"
            />
            {{ $t('configurator.save_offer') }}
          </b-button>
          <b-button
            variant="success"
            class="cfg-btn-lg btn-finish"
            :disabled="!canFinish"
            @click="openFinish"
          >
            <feather-icon
              icon="CheckIcon"
              size="14"
            />
            {{ $t('configurator.finish_offer') }}
          </b-button>
        </div>
        <!-- Lo que tiene que resolver quien hace la oferta -->
        <div
          v-if="errorList.length || todoList.length"
          class="alerts-block"
        >
          <div class="alerts-title">
            <feather-icon
              icon="AlertTriangleIcon"
              size="13"
            />
            {{ $t('configurator.alerts_todo') }}
          </div>
          <a
            v-for="error in errorList"
            :key="error.text"
            href="#"
            class="budget-warning budget-error todo-link"
            @click.prevent="goToError(error)"
          >
            {{ error.text }}
            <feather-icon
              v-if="error.step"
              icon="ChevronRightIcon"
              size="13"
              class="ml-auto"
            />
          </a>
          <a
            v-for="todo in todoList"
            :key="todo"
            href="#"
            class="budget-warning todo-link"
            @click.prevent="$emit('goto', 3)"
          >
            {{ todo }}
            <feather-icon
              icon="ChevronRightIcon"
              size="13"
              class="ml-auto"
            />
          </a>
        </div>
        <!-- Lo que completará Tecnitex al revisar la oferta -->
        <div
          v-if="tecnitexList.length"
          class="alerts-block"
        >
          <div class="alerts-title">
            <feather-icon
              icon="AlertCircleIcon"
              size="13"
            />
            {{ $t('configurator.alerts_tecnitex') }}
          </div>
          <div
            v-for="info in tecnitexList"
            :key="info"
            class="budget-note"
          >
            {{ info }}
          </div>
        </div>
      </div>

      <offer-totals
        :order="order"
        :packaging-editable="editable"
        @change-packaging="changePackaging"
      />
    </div>
    <b-modal
      ref="finishModal"
      :title="$t('configurator.delivery')"
      hide-footer
      size="lg"
      centered
    >
      <delivery-form
        v-if="order"
        :order="order"
        @confirm="finishOffer"
        @cancel="$refs.finishModal.hide()"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { BButton, BModal } from 'bootstrap-vue'
import DeliveryForm from './DeliveryForm.vue'
import OfferLines from './OfferLines.vue'
import OfferTotals from './OfferTotals.vue'
import {
  offerBoxes, offerLines, offerRows, translate,
} from '../offer'

export default {
  components: {
    BButton, BModal, DeliveryForm, OfferLines, OfferTotals,
  },
  computed: {
    ...mapGetters({
      order: 'configurator/getOrder',
      legacy: 'configurator/isLegacy',
      noBox: 'configurator/getNoBox',
      items: 'configurator/getItems',
      errors: 'configurator/getErrors',
      isItemComplete: 'configurator/isItemComplete',
      lang: 'languages/getCurrentLanguage',
    }),
    offerState() {
      return { items: this.items, noBox: this.noBox }
    },
    lines() {
      return offerLines(this.order)
    },
    boxRows() {
      return offerBoxes(this.order)
    },
    rows() {
      return offerRows(this.order, this.offerState)
    },
    editable() {
      return !this.legacy && (!this.order || this.order.status_last.type === 'pending')
    },
    subtitle() {
      const year = new Date(this.order.created_at * 1000).getFullYear()
      return [`${year}/T${this.order.id}`, this.order.client && this.order.client.name].filter(Boolean).join(' · ')
    },
    // Cada error lleva al paso donde se arregla: medidas → paso 2; cuadros y extras → paso 3
    errorList() {
      return Object.entries(this.errors).flatMap(([key, messages]) => messages.map(message => {
        const item = this.items.find(i => i.key === key)
        let step = null
        if (/^(CC|E)\d+$/.test(key)) step = 3
        else if (item) step = this.isItemComplete(item) ? 3 : 2
        return {
          text: key === 'general' ? message : `${key}: ${message}`, key, step,
        }
      }))
    },
    // Avisos que puede resolver quien hace la oferta
    todoList() {
      return this.rows.filter(row => row.needsBox && !row.box)
        .map(row => this.$t('configurator.warning_no_box', { key: row.line.type }))
    },
    // Avisos que resolverá Tecnitex en la revisión
    tecnitexList() {
      const list = []
      this.rows.filter(row => row.attr.price_pending).forEach(row => {
        list.push(this.$t('configurator.warning_curtain_price', { key: row.line.type, product: row.ref }))
      })
      this.boxRows.filter(b => b.attr.price_pending).forEach(box => {
        list.push(this.$t('configurator.warning_box_price', { key: box.line.type, product: box.line.product_object.ref }))
      })
      this.lines.filter(l => l.attr.tipo === 'extras' && l.attr.price_pending).forEach(extra => {
        const curtains = (extra.attr.cortinas || []).map(c => c.type).join(', ')
        list.push(this.$t('configurator.warning_extra_price', { key: curtains, product: this.tr(extra.line.product_object.name) }))
      })
      if (this.order.shipping_pending || !this.order.address.address) {
        list.push(this.$t('configurator.warning_transport'))
      }
      return list
    },
    canFinish() {
      return !!this.order && !this.errorList.length
    },
  },
  methods: {
    ...mapActions({
      save: 'configurator/save',
      finish: 'orders/finish',
      saveTransport: 'orders/saveTransport',
      getAddresses: 'addresses/getListAddress',
      getSelectProvinces: 'provinces/selectProvinces',
      getListCountries: 'countries/getListCountries',
      discardDraft: 'configurator/discardDraft',
    }),
    ...mapMutations({
      setPackagingType: 'configurator/setPackagingType',
      setFocus: 'configurator/setFocus',

      notifications: 'notifications',
      setShowNotifications: 'setShowNotifications',
    }),
    goToError(error) {
      if (!error.step) return
      if (error.step === 2) this.setFocus(error.key)
      this.$emit('goto', error.step)
    },
    tr(value) {
      return translate(value, this.lang)
    },
    async saveOffer(notify) {
      const saved = await this.save()
      if (saved && notify) {
        this.notifications({ title: this.$t('configurator.saved'), variant: 'success' })
        this.setShowNotifications(true)
      }
      return saved
    },
    async changePackaging(type) {
      this.setPackagingType(type)
      await this.saveOffer(false)
    },
    async openFinish() {
      if (!await this.saveOffer(false)) return
      // Como el tarificador anterior: el cuadro no es obligatorio, pero se avisa
      if (this.todoList.length) {
        const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('configurator.confirm_without_box'), {
          okTitle: this.$t('configurator.finish_offer'),
          cancelTitle: this.$t('configurator.cancel'),
          okVariant: 'success',
          cancelVariant: 'outline-secondary',
          footerClass: 'cfg-modal-footer',
          centered: true,
        })
        if (!confirmed) return
      }
      await Promise.all([
        this.getAddresses({
          page: 1, per_page: 999, search: '', client: this.order.client.id, type: 'shipping',
        }),
        this.getSelectProvinces({
          page: 1, per_page: 9999, search: '', orderBy: '',
        }),
        this.getListCountries({
          page: 1, per_page: 9999, search: '', orderBy: '',
        }),
      ])
      this.$refs.finishModal.show()
    },
    async finishOffer(addressId) {
      this.$refs.finishModal.hide()
      // Tarificador 2026: la API guarda la dirección y deja el transporte pendiente de cotizar
      await this.saveTransport({ order: this.order.id, address: addressId })
      await this.finish({ order: this.order.id, address: addressId, install: 'no' })
      this.discardDraft()
    },
  },
  async created() {
    if (this.editable && this.items.length) await this.saveOffer(false)
  },
}
</script>

<style lang="scss" scoped>
.summary-bottom {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 1.25rem;
  align-items: start;
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
}
.actions-title {
  margin-bottom: .75rem;
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: #82868b;
}
.btn-finish {
  border-color: #3a7d44 !important;
  background: #3a7d44 !important;
  background-image: none !important;
  &:hover:not(:disabled) {
    border-color: #15803d !important;
    background: #15803d !important;
  }
}
.todo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    background: #fde68a;
  }
}
.alerts-block {
  margin-top: 1rem;
}
.alerts-title {
  display: flex;
  align-items: center;
  gap: .375rem;
  margin-bottom: .5rem;
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: #82868b;
}
.actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .5rem;
}
</style>
